import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
  signOut,
  User as FirebaseUser
} from 'firebase/auth'
import { app } from '../services/firebase'

interface User {
  id: string
  email: string
  name: string | null
  role: 'admin' | 'partner' | 'consultant' | 'collaborator' | 'subcontractor' | 'client'
}

interface AuthContextValue {
  user: User | null
  firebaseUser: FirebaseUser | null
  loading: boolean
  error: string | null
  login: (email: string, password: string) => Promise<void>
  loginWithGoogle: () => Promise<void>
  signup: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  getToken: () => Promise<string | null>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const auth = getAuth(app)

  // Listen for Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser)

      // Always set loading true when auth state changes
      setLoading(true)

      if (fbUser) {
        // Fetch user data from our API
        try {
          const token = await fbUser.getIdToken()
          const response = await fetch('/api/auth/me', {
            headers: { Authorization: `Bearer ${token}` }
          })

          if (response.ok) {
            const userData = await response.json()
            setUser(userData)
          } else if (response.status === 404) {
            // User exists in Firebase but not in our DB yet - register them
            const registerResponse = await fetch('/api/auth/register', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
              },
              body: JSON.stringify({
                email: fbUser.email,
                name: fbUser.displayName
              })
            })

            if (registerResponse.ok) {
              const userData = await registerResponse.json()
              setUser(userData)
            } else {
              console.error('Failed to register user')
              setUser(null)
            }
          } else {
            setUser(null)
          }
        } catch (err) {
          console.error('Failed to fetch user data:', err)
          setUser(null)
        }
      } else {
        setUser(null)
      }

      setLoading(false)
    })

    return () => unsubscribe()
  }, [auth])

  const login = async (email: string, password: string) => {
    setError(null)
    try {
      await signInWithEmailAndPassword(auth, email, password)
    } catch (err: any) {
      setError(err.message || 'Login failed')
      throw err
    }
  }

  const loginWithGoogle = async () => {
    setError(null)
    try {
      const provider = new GoogleAuthProvider()
      await signInWithPopup(auth, provider)
    } catch (err: any) {
      setError(err.message || 'Google login failed')
      throw err
    }
  }

  const signup = async (email: string, password: string) => {
    setError(null)
    try {
      await createUserWithEmailAndPassword(auth, email, password)
    } catch (err: any) {
      setError(err.message || 'Signup failed')
      throw err
    }
  }

  const logout = async () => {
    setError(null)
    try {
      await signOut(auth)
      setUser(null)
    } catch (err: any) {
      setError(err.message || 'Logout failed')
      throw err
    }
  }

  const getToken = async (): Promise<string | null> => {
    if (!firebaseUser) return null
    return firebaseUser.getIdToken()
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        firebaseUser,
        loading,
        error,
        login,
        loginWithGoogle,
        signup,
        logout,
        getToken
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
